import React from 'react';
import {
  NewPageGlyph,
  NewFolderGlyph,
  RenameGlyph,
  DuplicateGlyph,
  FolderGlyph,
  PinGlyph,
  LinkGlyph,
  ExportGlyph,
  TrashGlyph,
  PrintGlyph,
  CloudGlyph,
  DeviceGlyph,
  PageGlyph,
} from './treeIcons';

const icon = (Glyph) => <Glyph size={16} className="doc-row-menu-icon" />;

/**
 * The ONE row menu definition (card 3.3), used by the row's "more" button,
 * right click and Shift+F10 alike — as `ui/ContextMenu` items.
 *
 *   Page:   New sub-page, Rename (F2), Duplicate, Move to…, Pin
 *           | Copy link, Export…
 *           | Move to trash
 *   Folder: New document, New folder, Rename (F2), Move to…
 *           | Export folder… (Markdown as a zip, or one PDF)
 *           | Move to trash
 *
 * New sub-page comes first so deep trees grow without the header buttons.
 * "Move to cloud / this device" (card C6) stays for top-level pages, next to
 * Move to…. `cmds` are the section's actions (see useRowCommands).
 */
export function rowMenuItems({ t, node, pinned, busy, location, cmds, projectRows = [] }) {
  const isFolder = node.type === 'folder';
  const disabledWhileBusy = busy != null;
  const rename = { icon: icon(RenameGlyph), label: t('docSidebar.menu.rename'), hint: 'F2', onClick: () => cmds.rename(node) };
  const moveTo = { icon: icon(FolderGlyph), label: t('docSidebar.menu.moveTo'), onClick: () => cmds.moveTo(node) };
  const trash = { icon: icon(TrashGlyph), label: t('docSidebar.menu.trash'), danger: true, onClick: () => cmds.trash(node) };

  if (isFolder) {
    return [
      { icon: icon(NewPageGlyph), label: t('docSidebar.menu.newDocument'), onClick: () => cmds.newDocument(node.id) },
      { icon: icon(NewFolderGlyph), label: t('docSidebar.menu.newFolder'), onClick: () => cmds.newFolder(node.id) },
      rename,
      moveTo,
      // Project scope (card 4.1): "Add to project ›" / "Remove from <project>".
      ...projectRows,
      { separator: true },
      {
        icon: icon(ExportGlyph),
        label: busy || t('docSidebar.menu.exportFolder'),
        disabled: disabledWhileBusy,
        items: [
          { icon: icon(PageGlyph), label: t('docSidebar.menu.exportFolderMarkdown'), onClick: () => cmds.exportFolderMarkdown(node) },
          { icon: icon(PageGlyph), label: t('docSidebar.menu.exportFolderPdf'), onClick: () => cmds.exportFolderPdf(node) },
        ],
      },
      { separator: true },
      trash,
    ];
  }

  return [
    { icon: icon(NewPageGlyph), label: t('docSidebar.menu.newSubpage'), onClick: () => cmds.newDocument(node.id) },
    rename,
    { icon: icon(DuplicateGlyph), label: t('docSidebar.menu.duplicate'), onClick: () => cmds.duplicate(node) },
    moveTo,
    ...projectRows,
    location?.canMove(node) && {
      icon: icon(location.onDevice ? CloudGlyph : DeviceGlyph),
      label: t(
        location.onDevice
          ? 'cloudLocal.moveToCloud'
          : location.action === 'download'
            ? 'cloudLocal.downloadToDevice'
            : 'cloudLocal.moveToDevice'
      ),
      disabled: location.moving,
      onClick: () => location.move(node),
    },
    { icon: icon(PinGlyph), label: pinned ? t('docSidebar.menu.unpin') : t('docSidebar.menu.pin'), onClick: () => cmds.togglePin(node) },
    { separator: true },
    { icon: icon(LinkGlyph), label: t('docSidebar.menu.copyLink'), onClick: () => cmds.copyLink(node) },
    {
      icon: icon(ExportGlyph),
      label: busy || t('docSidebar.menu.export'),
      disabled: disabledWhileBusy,
      items: [
        { icon: icon(PageGlyph), label: t('docSidebar.menu.exportPdf'), onClick: () => cmds.exportDocument(node, 'pdf') },
        { icon: icon(PageGlyph), label: t('docSidebar.menu.exportMarkdown'), onClick: () => cmds.exportDocument(node, 'markdown') },
        { icon: icon(PageGlyph), label: t('docSidebar.menu.exportHtml'), onClick: () => cmds.exportDocument(node, 'html') },
        { icon: icon(PrintGlyph), label: t('docSidebar.menu.print'), onClick: () => cmds.print(node) },
      ],
    },
    { separator: true },
    trash,
  ];
}
